import { SupportedLanguage } from "../../config/languages";
import { buildPageUrl } from "../../helpers/urlHelpers";
import classNames from "classnames/bind";
import Link from "next/link";
import { ButtonHTMLAttributes, ReactNode } from "react";
import styles from "./Button.module.css";

const cx = classNames.bind(styles);

type BaseButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Button content */
  children: ReactNode;
  /** Button variant */
  variant?:
    | "primary"
    | "secondary"
    | "secondary-inverted"
    | "text"
    | "text-inverted"
    | "iconbutton";
  /** Button size */
  size?: "small" | "medium";
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Custom CSS class */
  className?: string;
  /** Compact mode - removes padding and min-height constraints */
  compact?: boolean;
  /** Optional custom on click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

// When href is provided, locale is required
type ButtonWithHref = BaseButtonProps & {
  href: string;
  locale: SupportedLanguage;
};

// When href is not provided, locale is optional
type ButtonWithoutHref = BaseButtonProps & {
  href?: never;
  locale?: never;
};

export type ButtonProps = ButtonWithHref | ButtonWithoutHref;

export const Button = ({
  children,
  variant = "primary",
  size = "medium",
  fullWidth = false,
  loading = false,
  disabled = false,
  href,
  className,
  compact = false,
  onClick,
  locale,
  ...props
}: ButtonProps) => {
  const buttonClasses = cx("button", className);
  const buttonProps = {
    className: buttonClasses,
    "data-variant": variant,
    "data-size": size,
    "data-full-width": fullWidth,
    "data-loading": loading,
    "data-compact": compact,
  };

  const content = (
    <>
      {loading && <span className={cx("spinner")} />}
      <span className={cx("content")}>{children}</span>
    </>
  );

  if (href && !disabled && !loading) {
    const url = buildPageUrl(href, locale);
    return (
      <Link href={url} {...buttonProps}>
        {content}
      </Link>
    );
  }

  return (
    <button
      {...buttonProps}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
};
